import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { SortableContainer, SortableElement, SortableHandle, arrayMove } from 'react-sortable-hoc'
import { update, deepUpdate } from 'immupdate'
import { CustomCol } from './CustomCol'
import {
  Button,
  Checkbox,
  Col,
  Dropdown,
  Flex,
  Input,
  List,
  Modal,
  Row,
  Select,
  Space,
  Tooltip,
  Typography
} from 'antd'
import { CopyOutlined, DeleteOutlined, DownloadOutlined, HolderOutlined, PlusOutlined } from '@ant-design/icons'
import { HelloProps, ColItem, OptionItem } from '../interfaces'
import { getDefaultCnCols } from '../defaultCnCols'
import { v4 as uuidv4 } from 'uuid'
import { DefaultOptionType } from 'antd/lib/select'
import {
  fetchKeywordsData,
  getKeywordGroupsWithMembers,
  getKeywordSelectOptions,
  getKeywordsList,
} from './keywords/keywords-service'
import { KeywordGroupWithMembers } from './keywords/keywords-interfaces'
import KeywordGroupsToolbar from './KeywordGroupsToolbar'
import { rgeFilterValues } from './allQualifsRge'
import { copyToClipboard, handleDownloadCSV } from '../utils'
import { Agency, AgencyCriteriasGroup } from './agencies/agenciesInterfaces'
import {
  buildMsFilterOptions,
  MsEnseignesGroup,
  renderMsFilterOption,
  resolveMsSelectValues,
} from './ms/msEnseignesGroups-service'

declare var $: any

interface OptionColEntier {
  label: string
  value: string
  id: string
}

interface DynamicColumn {
  id: string
  name: string
  type: 'entier' | 'decimal' | 'text'
  params: {
    result: string
    condition: {
      [key: string]: {
        filter: string[]
        advanced: {}
      }
    }
  }[]
}

interface DynamicColumnsParam {
  id: string
  name: string
  cols: DynamicColumn[]
  dependencies: { id_col: string }[]
}

interface CnColumnsState {
  cols?: ColItem[]
  data: ColItem[]
  mutualizedDatabase: boolean
  mergedData: ColItem[]
  nameColObjectifVisite: string | null
  nameColsEntier: OptionColEntier[]
  globalCompetitorsOptions: OptionItem[]
  dynamicCols: DynamicColumnsParam[]
  keywordGroups: KeywordGroupWithMembers[]
  keywordsLoaded: boolean
  saving: boolean
}

export interface CnColumnsHandle {
  getCnColsData: () => string
}

interface SortableListProps {
  items: ColItem[]
  updateColElt: (col: ColItem) => void
  deleteCol: (col: ColItem) => void
  mutualizedDatabase: boolean
  nameColObjectifVisite: string | null
  nameColsEntier: OptionColEntier[]
  setNameColObjectifVisite: (newName: string, col: ColItem) => void
  globalCompetitorsOptions: OptionItem[]
  keywordGroups: KeywordGroupWithMembers[]
  keywordsLoaded: boolean
  agenciesData?: string
  msEnseignes: string[]
  msGroups: MsEnseignesGroup[]
}

interface SortableItemProps extends SortableListProps {
  cols: ColItem[]
  col: ColItem
}

function extractKeywordsFromGroup(group: AgencyCriteriasGroup | undefined): string[] {
  if (!group) return []

  const keywords: string[] = []

  if (group.criterias?.keywords) {
    keywords.push(...group.criterias.keywords)
  }
  if (group.criterias?.notKeywords) {
    keywords.push(...group.criterias.notKeywords)
  }

  if (group.group1) {
    keywords.push(...extractKeywordsFromGroup(group.group1))
  }
  if (group.group2) {
    keywords.push(...extractKeywordsFromGroup(group.group2))
  }

  return keywords
}

function extractQualifsFromGroup(group: AgencyCriteriasGroup | undefined): string[] {
  if (!group) return []

  const qualifs: string[] = []

  if (group.criterias?.qualif) {
    qualifs.push(...group.criterias.qualif)
  }

  if (group.group1) {
    qualifs.push(...extractQualifsFromGroup(group.group1))
  }
  if (group.group2) {
    qualifs.push(...extractQualifsFromGroup(group.group2))
  }

  return qualifs
}

export function getKeywordsUsedInAgencies(agenciesData?: string): string[] {
  try {
    if (!agenciesData) return []

    const agencies: Agency[] = JSON.parse(agenciesData).map((a: any) => ({
      ...a,
      formula: typeof a.formula === 'string' ? JSON.parse(a.formula) : a.formula,
    }))

    const allKeywords = new Set<string>()

    agencies.forEach((agency) => {
      if (agency.formula) {
        const formulaKeywords = extractKeywordsFromGroup(agency.formula.group1)
        formulaKeywords.forEach((kw) => allKeywords.add(kw))

        if (agency.formula.group2) {
          const group2Keywords = extractKeywordsFromGroup(agency.formula.group2)
          group2Keywords.forEach((kw) => allKeywords.add(kw))
        }
      }

      if (agency.importantKeywords) {
        agency.importantKeywords.forEach((kw) => allKeywords.add(kw))
      }
    })

    return Array.from(allKeywords).sort((a, b) => a.localeCompare(b))
  } catch (error) {
    console.error('Error extracting keywords from agencies:', error)
    return []
  }
}

function getQualifsUsedInAgencies(agenciesData?: string): string[] {
  try {
    if (!agenciesData) return []

    const agencies: Agency[] = JSON.parse(agenciesData).map((a: any) => ({
      ...a,
      formula: typeof a.formula === 'string' ? JSON.parse(a.formula) : a.formula,
    }))

    const allQualifs = new Set<string>()

    agencies.forEach((agency) => {
      if (agency.formula) {
        const formulaQualifs = extractQualifsFromGroup(agency.formula.group1)
        formulaQualifs.forEach((qualif) => allQualifs.add(qualif))

        if (agency.formula.group2) {
          const group2Qualifs = extractQualifsFromGroup(agency.formula.group2)
          group2Qualifs.forEach((qualif) => allQualifs.add(qualif))
        }
      }
    })

    return Array.from(allQualifs).sort((a, b) => a.localeCompare(b))
  } catch (error) {
    console.error('Error extracting qualifs from agencies:', error)
    return []
  }
}

export function getOptionsSelect(nameColumn: string, searchValue: string): DefaultOptionType[] {
  const msValues = ['cuisine', 'piscine', 'menuiserie', 'negoce', 'chauffage_bois']

  if (nameColumn === 'rge') {
    const filteredOptions = rgeFilterValues?.filter((option: DefaultOptionType) => {
      return String(option.label).toLowerCase().includes(searchValue.toLowerCase())
    })
    const sortedOptions = filteredOptions?.sort((a, b) => String(a.label).localeCompare(String(b.label)))
    return sortedOptions?.map((e: DefaultOptionType) => ({ label: e.label, value: e.value })) || []
  } else if (nameColumn === 'ms') {
    return msValues
      .sort((a, b) => a.localeCompare(b))
      .filter((option: string) => option.toLowerCase().includes(searchValue.toLowerCase()))
      .map((e: string) => ({ label: e, value: e }))
  } else if (nameColumn === 'keywords') {
    return getKeywordSelectOptions(searchValue)
  } else {
    return []
  }
}

const COLUMN_ROW_COLS = {
  drag: '32px',
  content: 'auto',
  displayInTable: '120px',
  filterInMap: '120px',
  readOnly: '100px',
} as const

const checkboxColStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-start',
  flexShrink: 0,
}

const DragHandle = SortableHandle(() => (
  <HolderOutlined style={{ color: 'rgba(51, 51, 51, .4)', cursor: 'grab' }} />
))

function ColumnListHeader() {
  return (
    <Row
      gutter={16}
      align="top"
      wrap={false}
      style={{ padding: '10px 0', borderBottom: '1px solid #efefef', width: '100%' }}
    >
      <Col flex={COLUMN_ROW_COLS.drag} />
      <Col flex={COLUMN_ROW_COLS.content} />
      <Col flex={COLUMN_ROW_COLS.displayInTable} style={checkboxColStyle}>
        <Typography.Text type="secondary">Afficher dans tableau</Typography.Text>
      </Col>
      <Col flex={COLUMN_ROW_COLS.readOnly} style={checkboxColStyle}>
        <Typography.Text type="secondary">Lecture seule</Typography.Text>
      </Col>
      <Col flex={"120px"} style={checkboxColStyle}>
        <Typography.Text type="secondary">Export dashboard</Typography.Text>
      </Col>
    </Row>
  )
}

function OverrideNameInput({
  value,
  onChange,
}: {
  value?: string
  onChange: (value: string | undefined) => void
}) {
  return (
    <Input
      style={{ width: '100%' }}
      placeholder="Nom de la colonne"
      value={value}
      onChange={(e) => {
        if (e.target.value.length > 0) {
          onChange(e.target.value)
        } else {
          onChange(undefined)
        }
      }}
    />
  )
}

function ColumnCheckboxes({
  cols,
  col,
  updateColElt,
}: {
  cols: ColItem[]
  col: ColItem
  updateColElt: (col: ColItem) => void
}) {
  return (
    <>
      <Col flex={"120px"} style={checkboxColStyle}>
        <Checkbox
          checked={col.displayInTable}
          onChange={(evt) => {
            const newCol = deepUpdate(col).at('displayInTable').set(evt.target.checked)
            updateColElt(newCol)
          }}
        />
      </Col>
      <Col flex={COLUMN_ROW_COLS.readOnly} style={checkboxColStyle}>
        {col.coltype === 'custom' && (
          <Checkbox
            checked={col.readOnly ?? false}
            onChange={(evt) => {
              const newCol = deepUpdate(col).at('readOnly').set(evt.target.checked)
              updateColElt(newCol)
            }}
          />
        )}
      </Col>
      <Col flex={"120px"} style={checkboxColStyle}>
        <Checkbox
          checked={col.exportInDashboardLogs ?? false}
          onChange={(evt) => {
            const oldCol = cols.find((c) => c.exportInDashboardLogs === true)
            if (oldCol) {
              const newCol = { ...oldCol }
              delete newCol.exportInDashboardLogs
              updateColElt(newCol)
            }
            const newCol = deepUpdate(col).at('exportInDashboardLogs').set(evt.target.checked)
            updateColElt(newCol)
          }}
        />
      </Col>
    </>
  )
}

function MultiSelectPopup({
  col,
  selectOptions,
  searchValue,
  setSearchValue,
  keywordGroups,
  agenciesData,
  updateColElt,
  menu,
}: {
  col: ColItem
  selectOptions: DefaultOptionType[]
  searchValue: string
  setSearchValue: (value: string) => void
  keywordGroups: KeywordGroupWithMembers[]
  agenciesData?: string
  updateColElt: (col: ColItem) => void
  menu: React.ReactElement
}) {
  const allOptions = getOptionsSelect(col.name, '')
  const allKeywordValues =
    col.name === 'keywords' ? getKeywordsList() : allOptions.map((option) => option.value as string)
  const isAllSelected =
    col.selectValue && allKeywordValues.length > 0 && allKeywordValues.length === col.selectValue.length
  const selectedCount = col.selectValue?.length || 0
  const keywordsUsedInAgencies = col.name === 'keywords' ? getKeywordsUsedInAgencies(agenciesData) : []
  const keywordsUsedCount = keywordsUsedInAgencies.length
  const qualifsUsedInAgencies = col.name === 'rge' ? getQualifsUsedInAgencies(agenciesData) : []
  const qualifsUsedCount = qualifsUsedInAgencies.length

  return (
    <div>
      <div style={{ padding: 8, borderBottom: '1px solid #f0f0f0' }}>
        <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 4, fontSize: 12 }}>
          {selectedCount} / {allKeywordValues.length} sélectionnés
        </Typography.Text>
        <div style={{ marginBottom: 4 }}>
          {col.name === 'keywords' && keywordsUsedCount > 0 && (
            <Tooltip title={`Sélectionner les ${keywordsUsedCount} mots-clés utilisés dans au moins une agence`}>
              <Button
                size="small"
                type="link"
                onClick={() => {
                  const current = col.selectValue || []
                  const toAdd = keywordsUsedInAgencies.filter((kw) => !current.includes(kw))
                  updateColElt(update(col, { selectValue: [...current, ...toAdd] }))
                }}
                style={{ padding: 0 }}
              >
                Sélectionner ceux des agences ({keywordsUsedCount})
              </Button>
            </Tooltip>
          )}
          {col.name === 'rge' && qualifsUsedCount > 0 && (
            <Tooltip title={`Sélectionner les ${qualifsUsedCount} qualifs utilisés dans au moins une agence`}>
              <Button
                size="small"
                type="link"
                onClick={() => {
                  const current = col.selectValue || []
                  const toAdd = qualifsUsedInAgencies.filter((qualif) => !current.includes(qualif))
                  updateColElt(update(col, { selectValue: [...current, ...toAdd] }))
                }}
                style={{ padding: 0 }}
              >
                Sélectionner ceux des agences ({qualifsUsedCount})
              </Button>
            </Tooltip>
          )}
        </div>
        <Flex gap={10} wrap="wrap">
          <Button
            size="small"
            type="link"
            onClick={() => {
              if (isAllSelected) {
                updateColElt(update(col, { selectValue: [] }))
              } else {
                const actualOptions = selectOptions.map((option) => option.value as string)
                const newSelectValue = Array.isArray(col.selectValue)
                  ? Array.from(new Set([...col.selectValue, ...actualOptions]))
                  : actualOptions
                updateColElt(update(col, { selectValue: newSelectValue }))
              }
              setSearchValue('')
            }}
            style={{ padding: 0 }}
          >
            {isAllSelected ? 'Désélectionner tout' : `Tout sélectionner (${selectOptions.length})`}
          </Button>
          {selectedCount > 0 && (
            <>
              <Tooltip title="Copier les éléments sélectionnés dans le presse-papiers">
                <Button
                  size="small"
                  type="link"
                  icon={<CopyOutlined />}
                  onClick={() => copyToClipboard(col.selectValue?.join('\n') || '')}
                  style={{ padding: 0 }}
                >
                  Copier
                </Button>
              </Tooltip>
              <Tooltip title="Télécharger les éléments sélectionnés au format CSV">
                <Button
                  size="small"
                  type="link"
                  icon={<DownloadOutlined />}
                  onClick={() =>
                    handleDownloadCSV(col.selectValue?.join('\n') || '', `${col.name}_selections.csv`)
                  }
                  style={{ padding: 0 }}
                >
                  Télécharger
                </Button>
              </Tooltip>
            </>
          )}
        </Flex>
      </div>
      {col.name === 'keywords' && (
        <KeywordGroupsToolbar
          groups={keywordGroups}
          selectedKeywords={col.selectValue || []}
          onSelectedKeywordsChange={(keywords) => {
            updateColElt(update(col, { selectValue: keywords }))
          }}
        />
      )}
      {menu}
    </div>
  )
}

function FournisseursConcurrentsSection({
  col,
  updateColElt,
}: {
  col: ColItem
  updateColElt: (col: ColItem) => void
}) {
  return (
    <Space direction="vertical" size="middle" style={{ width: '100%', marginTop: 16 }}>
      <Typography.Text>Informations supplémentaires (max. 4):</Typography.Text>
      <Flex vertical gap={8} style={{ maxHeight: 300, overflowY: 'auto' }}>
        {col.colSupp?.map((suppInfo: OptionItem, index: number) => (
          <Flex key={`coll-supp-${index}`} gap={10} align="center">
            <Input
              placeholder={"Ex: Chiffre d'affaire"}
              value={suppInfo.name}
              onChange={(e) => {
                const updatedColSupp = col.colSupp?.map((colSupp) =>
                  colSupp.id === suppInfo.id ? { ...colSupp, name: e.target.value } : colSupp,
                )
                updateColElt(update(col, { colSupp: updatedColSupp }))
              }}
            />
            <Button
              type="primary"
              danger
              icon={<DeleteOutlined />}
              onClick={() => {
                const updatedCollSupp = col.colSupp?.filter((colSupp) => colSupp.id !== suppInfo.id)
                updateColElt(update(col, { colSupp: updatedCollSupp }))
              }}
            >
              Supprimer
            </Button>
          </Flex>
        ))}
      </Flex>
      {(col.colSupp === undefined || col.colSupp.length < 4) && (
        <Button
          icon={<PlusOutlined />}
          onClick={() => {
            const newColSupp = { id: uuidv4(), name: '' }
            const updatedColSupp = col.colSupp ? [...col.colSupp, newColSupp] : [newColSupp]
            updateColElt(update(col, { colSupp: updatedColSupp }))
          }}
        >
          Ajouter une information supplémentaire
        </Button>
      )}

      <Typography.Text>Options :</Typography.Text>
      <Flex vertical gap={8} style={{ maxHeight: 300, overflowY: 'auto' }}>
        {col.options?.map((option: OptionItem, k: number) => (
          <Flex key={`competitor-option${k}`} gap={8} align="center">
            <Input
              placeholder="Option"
              value={option.name}
              onChange={(d) => {
                const updatedOptions = col.options?.map((opt) =>
                  opt.id === option.id ? { ...opt, name: d.target.value } : opt,
                )
                updateColElt(update(col, { options: updatedOptions }))
              }}
            />
            <Input
              placeholder="Label"
              value={option.label}
              onChange={(d) => {
                const updatedOptions = col.options?.map((opt) =>
                  opt.id === option.id ? { ...opt, label: d.target.value } : opt,
                )
                updateColElt(update(col, { options: updatedOptions }))
              }}
            />
            <Button
              type="primary"
              danger
              icon={<DeleteOutlined />}
              onClick={() => {
                const updatedOptions = col.options?.filter((opt) => opt.id !== option.id)
                updateColElt(update(col, { options: updatedOptions }))
              }}
            >
              Supprimer
            </Button>
          </Flex>
        ))}
      </Flex>
      <Button
        icon={<PlusOutlined />}
        onClick={() => {
          const newOption = { id: uuidv4(), name: '', label: '' }
          const updatedOptions = col.options ? [...col.options, newOption] : [newOption]
          updateColElt(update(col, { options: updatedOptions }))
        }}
      >
        Ajouter une option
      </Button>
    </Space>
  )
}

interface BaseColumnContentProps {
  col: ColItem
  updateColElt: (col: ColItem) => void
  mutualizedDatabase: boolean
  nameColObjectifVisite: string | null
  nameColsEntier: OptionColEntier[]
  setNameColObjectifVisite: (newName: string, col: ColItem) => void
  globalCompetitorsOptions: OptionItem[]
  keywordGroups: KeywordGroupWithMembers[]
  keywordsLoaded: boolean
  agenciesData?: string
  msEnseignes: string[]
  msGroups: MsEnseignesGroup[]
}

function BaseColumnContent({
  col,
  mutualizedDatabase,
  nameColObjectifVisite,
  nameColsEntier,
  setNameColObjectifVisite,
  globalCompetitorsOptions,
  keywordGroups,
  keywordsLoaded,
  agenciesData,
  updateColElt,
  msEnseignes,
  msGroups,
}: BaseColumnContentProps) {
  const [searchValue, setSearchValue] = useState<string>('')

  const msOptions = buildMsFilterOptions(msEnseignes, msGroups)

  const supplierMenuItems = useMemo(
    () =>
      globalCompetitorsOptions.length <= 0
        ? [{ key: 'empty', label: "Il n'y a pas de fournisseurs renseignés", disabled: true }]
        : globalCompetitorsOptions.map((option: OptionItem, index: number) => ({
          key: String(index),
          label: option.name,
        })),
    [globalCompetitorsOptions],
  )

  const selectOptions = useMemo(() => {
    console.log(col.name, searchValue, keywordsLoaded)
    return getOptionsSelect(col.name, searchValue)
  }, [col.name, searchValue, keywordsLoaded])

  const handleOverrideNameChange = (value: string | undefined) => {
    if (value !== undefined) {
      updateColElt(update(col, { overrideName: value }))
    } else {
      const temp = col
      delete temp.overrideName
      updateColElt(temp)
    }
  }

  return (
    <Row gutter={[16, 8]} align="middle" style={{ width: '100%', minWidth: 0 }}>
      <Col xs={24} sm={4} md={3} style={{ wordBreak: 'break-word' }}>
        <Typography.Text>{col.name}</Typography.Text>
      </Col>
      <Col xs={24} sm={2} md={1}>
        <Typography.Text type="secondary">{col.rank}</Typography.Text>
      </Col>

      {col.name === 'statut' && (
        <Col xs={24} sm={14} md={17}>
          <Checkbox
            checked={col.displayNonPertinentPopup ?? false}
            onChange={(e) => {
              updateColElt(update(col, { displayNonPertinentPopup: e.target.checked }))
            }}
          >
            Justification pour passage du statut en Non pertinent
          </Checkbox>
        </Col>
      )}

      {col.name === 'achats_accessibles' && (
        <Col xs={24} sm={14} md={17}>
          <Flex gap={16} wrap="wrap" align="center">
            <OverrideNameInput value={col.overrideName} onChange={handleOverrideNameChange} />
            <Checkbox
              checked={col.removeDisplayOfInitialValue ?? false}
              onChange={(e) => {
                updateColElt(update(col, { removeDisplayOfInitialValue: e.target.checked }))
              }}
            >
              Désactiver l'affichage de la valeur initiale
            </Checkbox>
          </Flex>
        </Col>
      )}

      {mutualizedDatabase && ['rdv'].includes(col.name) && (
        <Col xs={24} sm={14} md={17}>
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <Checkbox
              checked={col.removeAgenda ?? false}
              onChange={(e) => {
                updateColElt(update(col, { removeAgenda: e.target.checked }))
              }}
            >
              Désactiver les fonctionnalités agenda pour l'orga
            </Checkbox>
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              <Typography.Text>Col objectif visite :</Typography.Text>
              <Select
                placeholder="Col objectif visite"
                style={{ width: '100%' }}
                value={nameColObjectifVisite}
                options={nameColsEntier}
                onChange={(value: string) => {
                  setNameColObjectifVisite(value, col)
                }}
              />
            </Space>
          </Space>
        </Col>
      )}

      {mutualizedDatabase && ['rge', 'ms', 'fournisseurs_concurrents', 'keywords'].includes(col.name) && (
        <Col xs={24} sm={14} md={17} style={{ minWidth: 0 }}>
          <Flex gap={16} wrap="nowrap" align="start" style={{ width: '100%', minWidth: 0 }}>
            <div style={{ flex: '0 0 160px', minWidth: 120 }}>
              <OverrideNameInput value={col.overrideName} onChange={handleOverrideNameChange} />
            </div>
            {['rge', 'keywords'].includes(col.name) && (
              <div style={{ flex: 1, minWidth: 0 }}>
                <Select
                  autoClearSearchValue={false}
                  placeholder="Sélection"
                  mode="multiple"
                  style={{ width: '100%' }}
                  value={col.selectValue}
                  options={selectOptions}
                  filterOption={false}
                  onSearch={setSearchValue}
                  onChange={(value: string[]) => {
                    updateColElt(update(col, { selectValue: value }))
                  }}
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                  }}
                  onOpenChange={(open: boolean) => {
                    if (!open) {
                      setSearchValue('')
                    }
                  }}
                  maxTagCount={10}
                  popupRender={(menu) => (
                    <MultiSelectPopup
                      col={col}
                      selectOptions={selectOptions}
                      searchValue={searchValue}
                      setSearchValue={setSearchValue}
                      keywordGroups={keywordGroups}
                      agenciesData={agenciesData}
                      updateColElt={updateColElt}
                      menu={menu}
                    />
                  )}
                />
              </div>
            )}
            {col.name == "ms" && (
              <Select
                autoClearSearchValue={false}
                placeholder="Sélection"
                mode="multiple"
                allowClear
                showSearch
                style={{ width: '100%' }}
                value={col.selectValue || []}
                options={msOptions}
                filterOption={(input, option) =>
                  (option?.title ?? '').toString().toLowerCase().includes(input.toLowerCase())
                }
                optionRender={(option) =>
                  renderMsFilterOption(option.data, col.selectValue || [], msGroups)
                }
                menuItemSelectedIcon={null}
                onChange={(value: string[]) => {
                  updateColElt(update(col, { selectValue: resolveMsSelectValues(value || [], msGroups) }))
                }}
              />
            )}
            {['fournisseurs_concurrents'].includes(col.name) && (
              <Dropdown
                menu={{ items: supplierMenuItems }}
                trigger={['click']}
                overlayStyle={{ maxHeight: 200, overflowY: 'auto' }}
              >
                <Button>Liste de fournisseurs</Button>
              </Dropdown>
            )}
          </Flex>
        </Col>
      )}
    </Row>
  )
}

const SortableItem = SortableElement(
  ({
    cols,
    col,
    updateColElt,
    deleteCol,
    mutualizedDatabase,
    nameColObjectifVisite,
    nameColsEntier,
    setNameColObjectifVisite,
    globalCompetitorsOptions,
    keywordGroups,
    keywordsLoaded,
    agenciesData,
    msEnseignes,
    msGroups,
  }: SortableItemProps) => {
    return (
      <li style={{ listStyle: 'none', padding: '10px 0', borderBottom: '1px solid #efefef' }}>
        <Row gutter={16} align="top" wrap={false} style={{ width: '100%' }}>
          <Col flex={COLUMN_ROW_COLS.drag} style={{ flexShrink: 0 }}>
            <DragHandle />
          </Col>
          <Col flex={COLUMN_ROW_COLS.content} style={{ minWidth: 0 }}>
            {col.coltype !== 'custom' ? (
              <BaseColumnContent
                col={col}
                updateColElt={updateColElt}
                mutualizedDatabase={mutualizedDatabase}
                nameColObjectifVisite={nameColObjectifVisite}
                nameColsEntier={nameColsEntier}
                setNameColObjectifVisite={setNameColObjectifVisite}
                globalCompetitorsOptions={globalCompetitorsOptions}
                keywordGroups={keywordGroups}
                keywordsLoaded={keywordsLoaded}
                agenciesData={agenciesData}
                msEnseignes={msEnseignes}
                msGroups={msGroups}
              />
            ) : (
              <CustomCol
                col={col}
                updateCol={(c: ColItem) => updateColElt(c)}
                deleteCol={(c: ColItem) => deleteCol(c)}
              />
            )}
            {['fournisseurs_concurrents'].includes(col.name) && (
              <FournisseursConcurrentsSection col={col} updateColElt={updateColElt} />
            )}
          </Col>
          <ColumnCheckboxes cols={cols} col={col} updateColElt={updateColElt} />
        </Row>
      </li>
    )
  },
)

const SortableList = SortableContainer(
  ({
    items,
    updateColElt,
    deleteCol,
    mutualizedDatabase,
    nameColObjectifVisite,
    nameColsEntier,
    setNameColObjectifVisite,
    globalCompetitorsOptions,
    keywordGroups,
    keywordsLoaded,
    agenciesData,
    msEnseignes,
    msGroups,
  }: SortableListProps) => {
    return (
      <ul style={{ padding: 0, margin: 0 }}>
        <li style={{ listStyle: 'none' }}>
          <ColumnListHeader />
        </li>
        {items.map((value: ColItem, index: number) => (
          <SortableItem
            cols={items}
            key={`item-${index}`}
            index={index}
            col={value}
            items={items}
            updateColElt={updateColElt}
            deleteCol={deleteCol}
            mutualizedDatabase={mutualizedDatabase}
            nameColObjectifVisite={nameColObjectifVisite}
            nameColsEntier={nameColsEntier}
            setNameColObjectifVisite={setNameColObjectifVisite}
            globalCompetitorsOptions={globalCompetitorsOptions}
            keywordGroups={keywordGroups}
            keywordsLoaded={keywordsLoaded}
            agenciesData={agenciesData}
            msEnseignes={msEnseignes}
            msGroups={msGroups}
          />
        ))}
      </ul>
    )
  },
)

const REQUIRED_BASE_COLUMNS = [
  'ms',
  'rdv',
  'rge',
  'dep_number',
  'cs_fake',
  'cs',
  'fournisseurs_concurrents',
  'keywords',
  'effectif_site',
  'ape_code',
  'create_date',
] as const

function ensureBaseColumns(d: ColItem[]): ColItem[] {
  let lengthNotCustom = d.length
  const result = [...d]

  for (const name of REQUIRED_BASE_COLUMNS) {
    if (!result.find((col) => col.name === name)) {
      result.push({
        id: uuidv4(),
        coltype: 'coldebase',
        name,
        rank: lengthNotCustom,
        displayInTable: false,
        filterInMap: false,
        list_pdv_btn_filter: false,
        selectValue: name === 'keywords' ? [] : undefined,
        ...(name === 'fournisseurs_concurrents' ? { options: [] } : {}),
      })
      lengthNotCustom += 1
    }
  }

  return result
}

export const CnColumns = forwardRef<CnColumnsHandle, HelloProps>(function CnColumns(props, ref) {
  const [state, setState] = useState<CnColumnsState>({
    cols: [],
    data: [],
    mutualizedDatabase: true,
    mergedData: [],
    nameColObjectifVisite: null,
    nameColsEntier: [],
    globalCompetitorsOptions: [],
    dynamicCols: [],
    keywordGroups: [],
    keywordsLoaded: false,
    saving: false,
  })

  const getMergedData = useCallback((cols: ColItem[], data: ColItem[], currentNameColsEntier: OptionColEntier[]) => {
    console.log(data)
    let nameColsEntier = currentNameColsEntier

    const mergeObj: ColItem[] =
      (cols &&
        data &&
        cols
          .map((d: ColItem) => {
            if (d.custom?.inputtype == 'entier' && d.id) {
              nameColsEntier = [
                ...nameColsEntier.filter((e) => e.id != d.id),
                { label: d.custom.name, value: d.custom.name, id: d.id || '' },
              ]
            }
            return (
              data.find((k: ColItem) => d.coltype === 'coldebase' && k.name === d.name) ||
              update(d, {
                id: d.id ? d.id : uuidv4(),
              })
            )
          })
          .filter((d: ColItem) => d !== undefined)) ||
      []

    console.log('mergeObj', mergeObj)
    const mergeObj2 =
      mergeObj.find((e: ColItem) => e.rank !== undefined) !== undefined
        ? mergeObj.sort((a: ColItem, b: ColItem) => (a.rank || 0) - (b.rank || 0))
        : mergeObj
    console.log('Merged ', mergeObj2, mergeObj)

    return { mergedData: mergeObj2, nameColsEntier }
  }, [])

  const loadKeywords = useCallback(() => {
    fetchKeywordsData()
      .then(() => {
        setState((prev) => ({
          ...prev,
          keywordGroups: getKeywordGroupsWithMembers(),
          keywordsLoaded: true,
        }))
      })
      .catch(() => {
        setState((prev) => ({ ...prev, keywordsLoaded: true }))
      })
  }, [])

  const getDynamicCols = useCallback(() => {
    if (!props.cnSoftAccessId) return
    $.ajax({
      url: `/cnsoftsaccesses/dynamicCols?cnSoftAccessId=${props.cnSoftAccessId}`,
      type: 'GET',
      success: (d: DynamicColumnsParam[]) => {
        setState((prev) => {
          let nameColsEntier = [...prev.nameColsEntier]
          d.forEach((dynamicCol: DynamicColumnsParam) => {
            dynamicCol.cols.forEach((col: DynamicColumn) => {
              if (col.type === 'entier') {
                nameColsEntier = [...nameColsEntier, { label: col.name, value: col.name, id: col.id }]
              }
            })
          })

          let nameColObjectifVisite: string | null = null
          const id = prev.cols?.find((col) => col.objectifRdvColId)?.objectifRdvColId
          if (id) {
            nameColObjectifVisite =
              d.find((dynamicCol) => dynamicCol.cols.find((col) => col.id === id))?.cols.find((col) => col.id === id)
                ?.name || null
          }

          return {
            ...prev,
            dynamicCols: d,
            nameColsEntier,
            nameColObjectifVisite,
          }
        })
      },
      error: (e: {}) => {
        console.log(e)
      },
    })
  }, [props.cnSoftAccessId])

  const getCols = useCallback(() => {
    $.ajax({
      url: `/cnegoces/getGlobalCompetitors`,
      type: 'GET',
      success: (options: OptionItem[]) => {
        setState((prev) => ({ ...prev, globalCompetitorsOptions: options }))
      },
    })

    if (!props.cnSoftAccessId || props.cnSoftAccessId === 'new') {
      const cols = getDefaultCnCols()
      setState((prev) => {
        const { mergedData, nameColsEntier } = getMergedData(cols, prev.data, prev.nameColsEntier)
        return { ...prev, cols, mergedData, nameColsEntier }
      })
    } else {
      $.ajax({
        url: `/cnsoftsaccesses/cols?cnSoftAccessId=${props.cnSoftAccessId}`,
        type: 'GET',
        success: (d: ColItem[]) => {
          const colsWithBase = ensureBaseColumns(d)

          setState((prev) => {
            let nameColObjectifVisite: string | null = null
            const id = colsWithBase.find((col) => col.objectifRdvColId)?.objectifRdvColId
            if (id) {
              nameColObjectifVisite = colsWithBase.find((col) => col.id === id)?.custom?.name || null
              if (!nameColObjectifVisite && prev.dynamicCols.length > 0) {
                nameColObjectifVisite =
                  prev.dynamicCols
                    .find((dynamicCol) => dynamicCol.cols.find((col) => col.id === id))
                    ?.cols.find((col) => col.id === id)?.name || null
              }
            }

            const { mergedData, nameColsEntier } = getMergedData(colsWithBase, prev.data, prev.nameColsEntier)

            return {
              ...prev,
              cols: colsWithBase,
              mergedData,
              nameColsEntier,
              nameColObjectifVisite,
            }
          })
        },
        error: (e: {}) => {
          console.log(e)
        },
      })
    }
  }, [getMergedData, props.cnSoftAccessId])

  const isFirstRender = useRef(true)

  useEffect(() => {
    getCols()
    getDynamicCols()
    loadKeywords()
  }, [])

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }
    getCols()
    getDynamicCols()
  }, [props.cnSoftAccessId])

  const onSortEnd = useCallback(({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }) => {
    setState((prev) => {
      const newArray = arrayMove(prev.mergedData, oldIndex, newIndex)
      const newArray2 = newArray.map((d: ColItem) => update(d, { rank: newArray.indexOf(d) }))
      return { ...prev, mergedData: newArray2 }
    })
  }, [])

  const updateColElt = useCallback((col: ColItem) => {
    setState((prev) => {
      const newList = prev.mergedData.map((e) => (e.id === col.id ? col : e))
      const nextState: Partial<CnColumnsState> = { mergedData: newList }
      if (col.custom?.inputtype == 'entier' && col.id) {
        nextState.nameColsEntier = [
          ...prev.nameColsEntier.filter((e) => e.id != col.id),
          { label: col.custom.name, value: col.custom.name, id: col.id || '' },
        ]
      }
      return { ...prev, ...nextState }
    })
  }, [])

  const deleteCol = useCallback((col: ColItem) => {
    Modal.confirm({
      title: 'Supprimer cette colonne ?',
      content:
        "Voulez-vous vraiment supprimer cette colonne? Cette action est irréversible et affecte toute l'organisation",
      okText: 'Supprimer',
      okType: 'danger',
      cancelText: 'Annuler',
      onOk: () => {
        setState((prev) => ({
          ...prev,
          mergedData: prev.mergedData.filter((e) => e.id != col.id),
        }))
      },
    })
  }, [])

  const getCnColsData = useCallback((): string => {
    if (state.mergedData.length > 0) {
      return JSON.stringify(state.mergedData)
    }
    return JSON.stringify(getDefaultCnCols())
  }, [state.mergedData])

  useImperativeHandle(ref, () => ({ getCnColsData }), [getCnColsData])

  const setNameColObjectifVisite = useCallback(
    (newName: string, col: ColItem) => {
      if (newName == '') {
        delete col.objectifRdvColId
        updateColElt(col)
      } else {
        let idColObjectif = state.mergedData.find((item) => item.custom?.name === newName)?.id
        if (!idColObjectif && state.dynamicCols.length > 0) {
          idColObjectif = state.dynamicCols
            .find((dynamicCol) => dynamicCol.cols.find((item) => item.name === newName))
            ?.cols.find((item) => item.name === newName)?.id
        }
        console.log(newName, idColObjectif)
        updateColElt(update(col, { objectifRdvColId: idColObjectif }))
      }
      setState((prev) => ({ ...prev, nameColObjectifVisite: newName }))
    },
    [state.dynamicCols, state.mergedData, updateColElt],
  )

  const addCustomColumn = () => {
    setState((prev) => ({
      ...prev,
      mergedData: prev.mergedData.concat([
        {
          id: uuidv4(),
          coltype: 'custom',
          name: '',
          custom: {
            inputtype: 'text',
            name: '',
          },
          displayInTable: false,
          filterInMap: false,
          rank: prev.mergedData.length,
        },
      ]),
    }))
  }

  const dynamicColNames = state.dynamicCols.flatMap((dynamicCol) =>
    dynamicCol.cols.map((col) => col.name),
  )

  return (
    <div>
      {state.mergedData && (
        <SortableList
          msEnseignes={props.msEnseignes || []}
          msGroups={props.msGroups || []}
          items={state.mergedData}
          onSortEnd={onSortEnd}
          useDragHandle={true}
          updateColElt={updateColElt}
          deleteCol={deleteCol}
          mutualizedDatabase={state.mutualizedDatabase}
          nameColObjectifVisite={state.nameColObjectifVisite}
          nameColsEntier={state.nameColsEntier}
          setNameColObjectifVisite={setNameColObjectifVisite}
          globalCompetitorsOptions={state.globalCompetitorsOptions}
          keywordGroups={state.keywordGroups}
          keywordsLoaded={state.keywordsLoaded}
          agenciesData={props.initialAgencies}
        />
      )}

      <Button type="default" icon={<PlusOutlined />} onClick={addCustomColumn} style={{ marginTop: 16 }}>
        Ajouter colonne
      </Button>

      {state.dynamicCols.length > 0 &&
        state.dynamicCols.filter((dynamicCol) => dynamicCol.cols.length > 0).length > 0 && (
          <Space direction="vertical" size="middle" style={{ marginTop: 20, width: '100%' }}>
            <Typography.Title level={5} style={{ margin: 0 }}>
              Paramétrage - Colonnes dynamiques
            </Typography.Title>
            <Typography.Text>Colonnes dynamiques paramétrées dans l'outil :</Typography.Text>
            <List
              size="small"
              bordered
              dataSource={dynamicColNames}
              renderItem={(name) => <List.Item>{name}</List.Item>}
            />
          </Space>
        )}
    </div>
  )
})

export default CnColumns
